import type { ReactNode } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Button } from './Button'
import { cn } from '@/app/lib/cn'

/**
 * The frame every step shares: a title, the content, and a fixed action bar at
 * the bottom.
 *
 * The bar is pinned rather than following the content so the primary action is
 * always in the same place — an operator reaching for "Lanjut" without looking
 * finds it in the same spot on every step, and it stays within thumb reach on a
 * touch monitor.
 */
export function StepShell({
  title,
  hint,
  children,
  onBack,
  backLabel = 'Kembali',
  onNext,
  nextLabel = 'Lanjut',
  nextDisabled,
  nextBusy,
  status,
}: {
  title: string
  hint?: ReactNode
  children: ReactNode
  onBack?: () => void
  backLabel?: string
  onNext?: () => void
  nextLabel?: string
  nextDisabled?: boolean
  nextBusy?: boolean
  /** Shown between the two buttons — selection counts, warnings. */
  status?: ReactNode
}) {
  return (
    <div className="citaria-step-enter flex min-h-0 flex-1 flex-col">
      <header className="px-5 pb-3 pt-4 sm:px-8">
        <h1 className="text-[22px] font-semibold tracking-tight sm:text-[26px]">{title}</h1>
        {hint && <p className="mt-1 text-[14px] text-ink-muted">{hint}</p>}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 sm:px-8">{children}</div>

      <footer
        className={cn(
          'flex shrink-0 items-center gap-3 border-t border-line bg-surface/80 px-5 py-4 sm:px-8',
          'backdrop-blur supports-[padding:env(safe-area-inset-bottom)]:pb-[max(1rem,env(safe-area-inset-bottom))]',
        )}
      >
        {onBack ? (
          <Button variant="ghost" size="lg" onClick={onBack} shortcut="Esc">
            <ArrowLeft size={18} />
            {backLabel}
          </Button>
        ) : (
          <span />
        )}

        <div className="min-w-0 flex-1 text-center text-[14px] text-ink-muted">{status}</div>

        {onNext && (
          <Button
            size="lg"
            onClick={onNext}
            disabled={nextDisabled || nextBusy}
            shortcut="⏎"
            className="min-w-[160px]"
          >
            {nextBusy ? 'Memproses…' : nextLabel}
            {!nextBusy && <ArrowRight size={18} />}
          </Button>
        )}
      </footer>
    </div>
  )
}
